import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useOrg } from '../../../contexts/OrgContext'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import { useTheme } from '../../../contexts/ThemeContext'
import { getAdminNotifications, getNotificationColors, NOTIFICATION_TYPES } from '../../../shared/utils/notifications'
import { validatePassword } from '../../../shared/lib/passwordValidation'
import CommandPalette from '../../../shared/components/CommandPalette'
import { CLIENT_NAV_ITEMS } from './client/ClientVerticalNav'
import ClientSidebar from '../../../components/layout/ClientSidebar'
import { getSubCategory } from '../../../shared/data/industries'
import AccountSwitcher from '../../../shared/components/AccountSwitcher'

const navigation = [
  {
    name: 'Home',
    href: '/',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )
  },
  {
    name: 'Clients',
    href: '/clients',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )
  },
  {
    name: 'Policy Library',
    href: '/policies',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  {
    name: 'Regulatory News',
    href: '/regulatory-updates',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v18m0-18l-6 2l-3 9a5.002 5.002 0 006.001 0L6 5M12 3l6 2l3 9a5.002 5.002 0 01-6.001 0L18 5M8 21h8" />
      </svg>
    )
  },
  {
    name: 'Users',
    href: '/users',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    name: 'Audit Logs',
    href: '/audit-logs',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    )
  },
]

const settingsItem = {
  name: 'Settings',
  href: '/settings',
  icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function Layout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, profile, signOut } = useAuth()
  const { clients } = useAssessment()
  const { currentOrg, userOrgRole } = useOrg()
  const { isDemoOrg } = useDemoPlan()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('sidebar-pinned') === 'true')
  const [sidebarHovered, setSidebarHovered] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const sidebarExpanded = sidebarPinned || sidebarHovered

  const sidebarCollapsed = !sidebarExpanded
  const [sidebarCollapsedSections, setSidebarCollapsedSections] = useState({})
  const [showDropdown, setShowDropdown] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedNotifications') || '[]')
    } catch {
      return []
    }
  })
  const dropdownRef = useRef(null)
  const notificationRef = useRef(null)

  // Get notifications
  const notifications = getAdminNotifications(clients)
  const unreadCount = notifications.filter(n => !dismissedNotifications.includes(n.id)).length

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Dismiss a notification
  const dismissNotification = (notificationId) => {
    const updated = [...dismissedNotifications, notificationId]
    setDismissedNotifications(updated)
    localStorage.setItem('dismissedNotifications', JSON.stringify(updated))
  }

  // Clear all dismissed notifications (to show them again)
  const resetDismissedNotifications = () => {
    setDismissedNotifications([])
    localStorage.removeItem('dismissedNotifications')
  }


  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Command palette keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Command palette navigation items
  // Detect client view: either /clients/:id paths or /assessment/:id paths
  const assessmentMatch = location.pathname.match(/^\/assessment\/([^/]+)/)
  const assessmentClientId = useMemo(() => {
    if (!assessmentMatch) return null
    const aId = assessmentMatch[1]
    for (const c of clients) {
      if (c.assessments?.some(a => a.id === aId)) return c.id
    }
    return null
  }, [assessmentMatch?.[1], clients])
  const isInClientView = (location.pathname.startsWith('/clients/') && location.pathname !== '/clients') || !!assessmentClientId
  const clientBasePath = assessmentClientId ? `/clients/${assessmentClientId}` : location.pathname
  const currentClientId = assessmentClientId || (isInClientView ? location.pathname.split('/clients/')[1]?.split('/')[0]?.split('?')[0] : null)
  const currentClient = useMemo(() => currentClientId ? clients.find(c => c.id === currentClientId) : null, [currentClientId, clients])
  const paletteNavigation = useMemo(() => navigation.map(n => ({ name: n.name, href: n.href })), [])
  const paletteClients = useMemo(() => clients.map(c => ({ id: c.id, name: c.name, industry: c.industry })), [clients])

  const handleSignOut = async () => {
    setShowDropdown(false)
    await signOut()
    navigate('/login')
  }

  const passwordValidation = useMemo(() => validatePassword(newPassword), [newPassword])
  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (!passwordValidation.valid) {
      setPasswordError('Please fix the password requirements below')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setChangingPassword(true)
    try {
      const { supabase } = await import('../../../shared/lib/supabase')
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error

      // Refresh the session after password change to avoid stale token errors
      await supabase.auth.refreshSession()

      // Audit log is best-effort — don't block on failure
      try {
        const { logAuditEvent } = await import('../../../shared/lib/auditLog')
        logAuditEvent({
          category: 'system',
          action: 'password.changed',
          description: 'User changed their password'
        })
      } catch {}

      setPasswordSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => {
        setShowPasswordModal(false)
        setPasswordSuccess(false)
      }, 2000)
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password')
    } finally {
      setChangingPassword(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — matches login page palette */}
      <aside
        className={`
          fixed top-0 left-0 h-full
          z-50
          ${sidebarExpanded ? 'w-64' : 'w-16'}
          transform transition-all duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ backgroundColor: '#012438', borderRight: '1px solid #3F5A6A' }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-16 ${sidebarCollapsed ? 'px-3 justify-center' : 'px-6'}`}>
            <Link to="/" className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
              <img src="/logo.png" alt="SSA Logo" className="w-9 h-9 rounded-xl object-contain flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-bold text-white tracking-wide">Vantage</span>
                    <span className="text-[9px] font-normal" style={{ color: '#6B7F8E' }}>by Sidewalk Security</span>
                  </div>
                  <span className="text-[8px] font-medium uppercase tracking-wider" style={{ color: '#8A9BAA' }}>Executive Advisory Platform</span>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation — switches between global nav and client nav */}
          <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 space-y-1 overflow-y-auto scrollbar-hide`}>
            {isInClientView ? (
              <>
                {/* Back to clients */}
                <Link
                  to="/clients"
                  title={sidebarCollapsed ? 'Back to Clients' : undefined}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2' : 'px-3 py-2'} rounded-lg transition-colors mb-1`}
                  style={{ color: '#7E8F9C' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#8AD0D6'; e.currentTarget.style.backgroundColor = '#213C4E' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#7E8F9C'; e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                  </svg>
                  {!sidebarCollapsed && <span className="ml-3 text-sm font-medium">Back to Clients</span>}
                </Link>
                <div className="mx-3 my-2" style={{ borderTop: '1px solid #3F5A6A' }} />
                {/* Client sidebar navigation */}
                {!sidebarCollapsed && currentClient && (() => {
                  const isNewAssessment = location.pathname.includes('/new-assessment')
                  const activeSection = assessmentMatch || isNewAssessment
                    ? 'maturity-assessment'
                    : new URLSearchParams(location.search).get('section') || 'overview'
                  const latestAssessment = (() => {
                    const completed = (currentClient.assessments || []).filter(a => a.status === 'completed').sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
                    return completed[0] || (currentClient.assessments || []).find(a => a.status === 'in_progress') || null
                  })()
                  const clientBasePath = `/clients/${currentClient.id}`
                  return (
                    <ClientSidebar
                      client={currentClient}
                      assessment={latestAssessment}
                      activeSection={activeSection}
                      onNavigate={(key) => {
                        navigate(`${clientBasePath}?section=${key}`)
                      }}
                    />
                  )
                })()}

                {/* Legacy: collapsed sidebar shows old nav */}
                {sidebarCollapsed && CLIENT_NAV_ITEMS.map((group, gi) => {
                  if (group.standalone) {
                    const activeSection = new URLSearchParams(location.search).get('section') || 'overview'
                    const isActive = activeSection === group.key
                    return (
                      <Link
                        key={group.key}
                        to={`${clientBasePath}?section=${group.key}`}
                        title={sidebarCollapsed ? group.label : undefined}
                        className={`flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2'} rounded-lg text-[13.5px] font-medium transition-all duration-200`}
                        style={isActive ? { backgroundColor: '#213C4E', color: '#ffffff' } : { color: '#BCC5CD' }}
                        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                      >
                        <span className={`${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0`} style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            {group.label}
                            {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />}
                          </>
                        )}
                      </Link>
                    )
                  }

                  const activeSection = new URLSearchParams(location.search).get('section') || 'overview'

                  return (
                    <div key={gi}>
                      {/* Section label */}
                      {!sidebarCollapsed && (
                        <p className={`px-3 ${gi > 0 ? 'pt-4' : 'pt-2'} pb-1 text-[13px] font-semibold uppercase tracking-widest`} style={{ color: '#5A7080' }}>
                          {group.section}
                        </p>
                      )}
                      {gi > 0 && sidebarCollapsed && <div className="mx-3 my-2" style={{ borderTop: '1px solid rgba(63, 90, 106, 0.4)' }} />}

                      {/* Items */}
                      {group.items.map(item => {
                        if (item.comingSoon) {
                          if (sidebarCollapsed) return null
                          return (
                            <div key={item.key} className="flex items-center px-3 py-1.5 text-xs" style={{ color: '#5A7080' }}>
                              <span className="mr-3 flex-shrink-0 opacity-50">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                                </svg>
                              </span>
                              {item.label}
                              <span className="ml-auto text-[10px] px-1.5 py-px rounded" style={{ backgroundColor: '#213C4E' }}>Soon</span>
                            </div>
                          )
                        }

                        // Items with children — collapsible parent
                        if (item.children) {
                          const childActive = item.children.some(c => activeSection === c.key)
                          const isCollapsed = sidebarCollapsedSections?.[item.key]
                          if (sidebarCollapsed) {
                            // Collapsed sidebar — show parent icon only, navigate to first child
                            return (
                              <Link
                                key={item.key}
                                to={`${clientBasePath}?section=${item.children[0].key}`}
                                title={item.label}
                                className="flex items-center justify-center py-2.5 rounded-lg text-[13.5px] font-medium transition-all duration-200"
                                style={childActive ? { backgroundColor: '#213C4E', color: '#ffffff' } : { color: '#BCC5CD' }}
                              >
                                <span style={{ color: childActive ? '#8AD0D6' : '#7E8F9C' }}>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                                  </svg>
                                </span>
                              </Link>
                            )
                          }
                          return (
                            <div key={item.key}>
                              <button
                                onClick={() => setSidebarCollapsedSections(prev => ({ ...prev, [item.key]: !prev?.[item.key] }))}
                                className="w-full flex items-center px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-all duration-200"
                                style={childActive ? { color: '#ffffff' } : { color: '#BCC5CD' }}
                                onMouseEnter={(e) => { if (!childActive) { e.currentTarget.style.color = '#ffffff' } }}
                                onMouseLeave={(e) => { if (!childActive) { e.currentTarget.style.color = childActive ? '#ffffff' : '#BCC5CD' } }}
                              >
                                <span className="mr-3 flex-shrink-0" style={{ color: childActive ? '#8AD0D6' : '#7E8F9C' }}>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                                  </svg>
                                </span>
                                {item.label}
                                <svg className={`w-3 h-3 ml-auto transition-transform duration-150 ${isCollapsed ? '-rotate-90' : ''}`} style={{ color: '#7E8F9C' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {!isCollapsed && item.children.map(child => {
                                if (child.comingSoon) {
                                  return (
                                    <div key={child.key} className="flex items-center pl-11 pr-3 py-1 text-[13px]" style={{ color: '#5A7080' }}>
                                      {child.label}
                                      <span className="ml-auto text-[10px] px-1.5 py-px rounded" style={{ backgroundColor: '#213C4E' }}>Soon</span>
                                    </div>
                                  )
                                }
                                const isChildActive = activeSection === child.key
                                return (
                                  <Link
                                    key={child.key}
                                    to={`${clientBasePath}?section=${child.key}`}
                                    className="flex items-center pl-11 pr-3 py-1 rounded-md text-xs font-medium transition-all duration-200"
                                    style={isChildActive ? { backgroundColor: '#213C4E', color: '#ffffff' } : { color: '#8A9BAA' }}
                                    onMouseEnter={(e) => { if (!isChildActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                                    onMouseLeave={(e) => { if (!isChildActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#8A9BAA' } }}
                                  >
                                    {child.label}
                                    {isChildActive && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />}
                                  </Link>
                                )
                              })}
                            </div>
                          )
                        }

                        const isActive = activeSection === item.key
                        return (
                          <Link
                            key={item.key}
                            to={`${clientBasePath}?section=${item.key}`}
                            title={sidebarCollapsed ? item.label : undefined}
                            className={`flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-1.5'} rounded-lg text-[13.5px] font-medium transition-all duration-200`}
                            style={isActive ? { backgroundColor: '#213C4E', color: '#ffffff' } : { color: '#BCC5CD' }}
                            onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                            onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                          >
                            <span className={`${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0`} style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {item.iconPaths ? item.iconPaths.map((d, i) => (
                                  <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} />
                                )) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                                )}
                              </svg>
                            </span>
                            {!sidebarCollapsed && (
                              <>
                                {item.label}
                                {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />}
                              </>
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  )
                })}
              </>
            ) : (
              /* Global nav */
              navigation.map((item) => {
                const isActive = item.href === '/clients'
                  ? location.pathname === '/clients' || location.pathname.startsWith('/clients/')
                  : location.pathname === item.href

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={sidebarCollapsed ? item.name : undefined}
                    className={`
                      flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                      transition-all duration-200 group relative
                    `}
                    style={isActive
                      ? { backgroundColor: '#213C4E', color: '#ffffff' }
                      : { color: '#BCC5CD' }
                    }
                    onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                    onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                  >
                    <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors flex-shrink-0`}
                      style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                      {item.icon}
                    </span>
                    {!sidebarCollapsed && (
                      <>
                        {item.name}
                        {isActive && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />
                        )}
                      </>
                    )}
                  </Link>
                )
              })
            )}
          </nav>

          {/* Settings — hidden in client view */}
          {!isInClientView && (
          <>
          {/* Settings - pinned to bottom */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} pb-1`}>
            {(() => {
              const isActive = location.pathname === settingsItem.href
              return (
                <Link
                  to={settingsItem.href}
                  title={sidebarCollapsed ? settingsItem.name : undefined}
                  className={`
                    flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} rounded-lg text-sm font-medium
                    transition-all duration-200 group
                  `}
                  style={isActive
                    ? { backgroundColor: '#213C4E', color: '#ffffff' }
                    : { color: '#BCC5CD' }
                  }
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BCC5CD' } }}
                >
                  <span className={`${sidebarCollapsed ? '' : 'mr-3'} transition-colors flex-shrink-0`}
                    style={{ color: isActive ? '#8AD0D6' : '#7E8F9C' }}>
                    {settingsItem.icon}
                  </span>
                  {!sidebarCollapsed && (
                    <>
                      {settingsItem.name}
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />
                      )}
                    </>
                  )}
                </Link>
              )
            })()}
          </div>
          </>
          )}

          {/* Account Switcher (Global Admin only) */}
          <AccountSwitcher collapsed={sidebarCollapsed} currentView="admin" />

          {/* Expand / Collapse toggle */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-3`}>
            <div className="mx-2 mb-2" style={{ borderTop: '1px solid rgba(63, 90, 106, 0.4)' }} />
            <button
              onClick={() => {
                const next = !sidebarPinned
                setSidebarPinned(next)
                localStorage.setItem('sidebar-pinned', next ? 'true' : 'false')
                if (!next) setSidebarHovered(false)
              }}
              className="w-full flex items-center justify-center px-3 py-2 rounded-lg text-sm transition-colors"
              style={{ color: '#7E8F9C' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#BCC5CD' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#7E8F9C' }}
              title={sidebarPinned ? 'Collapse sidebar' : 'Sidebar control'}
            >
              {sidebarCollapsed ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              ) : (
                <>
                  <svg className={`w-5 h-5 transition-transform duration-300 ${sidebarPinned ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                  <span className="ml-3">{sidebarPinned ? 'Collapse' : 'Expand'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className={`${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'} flex flex-col min-h-screen transition-all duration-200`}>
        {/* Demo mode banner */}
        {isDemoOrg && (
          <div className="sticky top-0 z-40 flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            You are using a Demo account. Some features are restricted.
            <a href="mailto:support@sidewalksecurity.com" className="underline hover:no-underline font-semibold">Contact us to upgrade</a>
          </div>
        )}
        {/* Top bar */}
        <header data-main-header className={`sticky ${isDemoOrg ? 'top-[30px]' : 'top-0'} z-30 bg-white/80 dark:bg-gray-900 backdrop-blur-md dark:backdrop-blur-none border-b border-gray-200 dark:border-gray-700`}>
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left side - Mobile menu + org name */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {currentOrg && (
                <div className="hidden lg:flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    {currentOrg.name}
                  </span>
                  <span className="text-[9px] font-medium uppercase tracking-wider text-gray-300 dark:text-gray-600">
                    Admin Portal
                  </span>
                </div>
              )}
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center space-x-2">

              {/* Search trigger */}
              <button
                onClick={() => setCommandPaletteOpen(true)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-500 hover:text-gray-500 dark:hover:text-gray-400 transition-colors w-52"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-sm flex-1 text-left">Search...</span>
                <span className="flex items-center gap-0.5 text-[11px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-medium">
                  <span>{/Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'}</span><span>K</span>
                </span>
              </button>

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Notifications"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      <div className="flex items-center gap-3">
                        {unreadCount > 0 && (
                          <button
                            onClick={() => {
                              const allIds = notifications.map(n => n.id)
                              const updated = [...new Set([...dismissedNotifications, ...allIds])]
                              setDismissedNotifications(updated)
                              localStorage.setItem('dismissedNotifications', JSON.stringify(updated))
                            }}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                          >
                            Dismiss all
                          </button>
                        )}
                        {dismissedNotifications.length > 0 && (
                          <button
                            onClick={resetDismissedNotifications}
                            className="text-xs text-primary-600 dark:text-accent-300 hover:underline"
                          >
                            Show dismissed
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <p className="text-sm text-gray-500 dark:text-gray-400">No notifications</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {notifications.map(notification => {
                            const isDismissed = dismissedNotifications.includes(notification.id)
                            const colors = getNotificationColors(notification.type)

                            if (isDismissed) return null

                            return (
                              <div
                                key={notification.id}
                                className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 ${colors.border}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${colors.iconBg} flex items-center justify-center`}>
                                    {notification.type === NOTIFICATION_TYPES.BENCHMARK_OVERDUE && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    )}
                                    {notification.type === NOTIFICATION_TYPES.THREAT_DATA_STALE && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
                                      </svg>
                                    )}
                                    {notification.type === NOTIFICATION_TYPES.BENCHMARK_REFRESH && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    )}
                                    {notification.type === NOTIFICATION_TYPES.STALE_ASSESSMENT && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    )}
                                    {notification.type === NOTIFICATION_TYPES.REGULATORY_UPDATE && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 0l-6 2l-3 9a5 5 0 006 0L6 7m6-3l6 2l3 9a5 5 0 01-6 0L18 7m-6-4v17m-4 0h8" />
                                      </svg>
                                    )}
                                    {notification.type === NOTIFICATION_TYPES.INFO && (
                                      <svg className={`w-4 h-4 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      {notification.title}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                      {notification.message}
                                    </p>
                                    <div className="mt-2 flex items-center gap-3">
                                      {notification.actionLabel && (
                                        <Link
                                          to={notification.actionUrl}
                                          onClick={() => setShowNotifications(false)}
                                          className="text-xs font-medium text-primary-600 dark:text-accent-300 hover:underline"
                                        >
                                          {notification.actionLabel}
                                        </Link>
                                      )}
                                      <button
                                        onClick={() => dismissNotification(notification.id)}
                                        className="text-xs text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                      >
                                        Dismiss
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Benchmark data v{notifications[0]?.metadata?.version || '2.0'} • Last updated{' '}
                          {notifications[0]?.metadata?.lastUpdated
                            ? new Date(notifications[0].metadata.lastUpdated).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {showDropdown && (
                  <div className="absolute right-0 mt-2 min-w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{profile?.full_name || 'User'}</p>
                      {(userOrgRole || profile?.role) && (
                        <p className="text-[11px] font-medium text-primary-600 dark:text-primary-400 mt-0.5 whitespace-nowrap">
                          {(userOrgRole || profile.role).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowDropdown(false)
                          setShowPasswordModal(true)
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Change Password
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className={`flex-1 ${isInClientView ? '' : 'p-4 sm:p-6 lg:p-8'}`}>
          <div className={isInClientView ? '' : 'max-w-7xl mx-auto'}>
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white/50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              Sidewalk Security Advisors • Executive Advisory Platform
            </p>
          </div>
        </footer>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Change Password</h2>
              <button
                onClick={() => {
                  setShowPasswordModal(false)
                  setNewPassword('')
                  setConfirmPassword('')
                  setPasswordError('')
                  setPasswordSuccess(false)
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {passwordSuccess ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-600 dark:text-green-400 font-medium">Password updated successfully!</p>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                {/* Password requirements checklist */}
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p className="font-medium mb-1.5">Password requirements:</p>
                  {passwordValidation.checks.filter(c => c.id !== 'maxLength').map(check => (
                    <div key={check.id} className="flex items-center gap-2">
                      {newPassword.length === 0 ? (
                        <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                      ) : check.passed ? (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={newPassword.length === 0 ? '' : check.passed ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                        {check.label}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    {confirmPassword.length === 0 ? (
                      <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                    ) : passwordsMatch ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={confirmPassword.length === 0 ? '' : passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                      Passwords match
                    </span>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false)
                      setNewPassword('')
                      setConfirmPassword('')
                      setPasswordError('')
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword || !passwordValidation.valid || !passwordsMatch}
                    className="btn-primary"
                  >
                    {changingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Command palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        clients={paletteClients}
        navigation={paletteNavigation}
      />
    </div>
  )
}

export default Layout
